import { fileToBase64 } from "../hooks/useLocalStorage";

// A panel never needs more than this many pixels on its long edge: a full
// Letter page at 300 DPI is 3300px, and most panels are a fraction of that.
const MAX_EDGE_PX = 2400;
// Files under this size are kept exactly as they are
const KEEP_ORIGINAL_BYTES = 1.2 * 1024 * 1024;
const JPEG_QUALITY = 0.9;

export const isImageFile = (file) => Boolean(file && file.type && file.type.startsWith("image/"));

/** First image file in a drop or paste, if any. */
export const imageFromDataTransfer = (dataTransfer) => {
  if (!dataTransfer) return null;
  const files = Array.from(dataTransfer.files || []);
  const fromFiles = files.find(isImageFile);
  if (fromFiles) return fromFiles;
  const items = Array.from(dataTransfer.items || []);
  const item = items.find((i) => i.kind === "file" && i.type.startsWith("image/"));
  return item ? item.getAsFile() : null;
};

/** True while a drag carries files (before we're allowed to read them). */
export const dragHasFiles = (dataTransfer) =>
  Boolean(dataTransfer && Array.from(dataTransfer.types || []).includes("Files"));

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error("Could not read that image"));
  img.src = url;
});

/**
 * Read an image file as a data URL for a panel background, downscaling big
 * photos so a single drop can't fill the browser's storage. PNGs stay PNG
 * (they may be transparent line art); everything else becomes JPEG.
 */
export const prepareImageForPanel = async (file) => {
  if (!isImageFile(file)) throw new Error("That file isn't an image");
  const original = await fileToBase64(file);
  if (file.size <= KEEP_ORIGINAL_BYTES || file.type === "image/svg+xml") return original;

  let img;
  try {
    img = await loadImage(original);
  } catch (e) {
    return original;
  }
  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = Math.min(1, MAX_EDGE_PX / longEdge);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return original;
  const asPng = file.type === "image/png";
  if (!asPng) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const resized = canvas.toDataURL(asPng ? "image/png" : "image/jpeg", JPEG_QUALITY);
  return resized.length < original.length ? resized : original;
};
